import { getSessionStorage, setSessionStorage, clearSessionStorage } from './sessionStorageHelper';

// Allow-listed rather than excluded, so a field added later cannot leak in by default.
// Country of citizenship is the only identity input the page still renders. Left out on
// purpose: the SSN and the date of birth, which would sit in plaintext storage; and
// every server-derived value, above all countryOfResidence, which mapToIdentity uses to
// decide whether the SSN is submitted at all - a stale copy of that must never win over
// what Ellis returns.
const IDENTITY_FIELDS = ['countryOfCitizenship'];

// countryName and regionName are display-only values derived when the server state is
// mapped, so they are re-derived rather than restored.
const ADDRESS_FIELDS = {
  residential: [
    'country', 'address1', 'address2', 'city', 'region', 'zip',
    'dontContact', 'sameAddress', 'copyFromPrimary',
  ],
  mailing: ['country', 'address1', 'address2', 'city', 'region', 'zip', 'copyFromPrimary'],
};

function pickFields(source, fields) {
  const result = {};
  fields.forEach((field) => {
    if (source && source[field] !== undefined) result[field] = source[field];
  });
  return result;
}

export const storageKey = (applicationId) => { return `aop-personal-info-auto-save-${applicationId}`; };

// The saved values are snapshotted on first read and never refreshed from storage, so
// that auto-saving cannot feed back into initialValues and retrigger
// enableReinitialize while the user is still typing.
let snapshotApplicationId = null;
let snapshot = null;

// Entries are keyed by contact, never by array position: mapToAddress copies
// primary -> joint by index, so an index shift could otherwise land the primary's
// values in the co-applicant's slot. If a contact is replaced the saved entry is
// orphaned and dropped, which is the safe failure.
//
// The country of residence is part of the key because it decides which fields are
// locked and how the address is validated. If CRM changes it, the key stops matching
// and the values saved for the old country are dropped rather than restored into a
// form that now describes a different one.
function contactId(entry) {
  return entry && entry.contact && entry.contact.id
    ? `${entry.contact.id}:${entry.contact.countryOfResidence || ''}`
    : null;
}

// Only entries with no server row are auto-saved. Anything already persisted is
// re-read from the server on refresh and must win over the auto-saved values.
function keyByContact(entries, pick) {
  const result = {};
  (entries || []).forEach((entry) => {
    const key = contactId(entry);
    if (key && !entry.id) result[key] = pick(entry);
  });
  return result;
}

function identityFields(identity) {
  return pickFields(identity, IDENTITY_FIELDS);
}

function isPrimary(entry) {
  return !!(entry && entry.contact && entry.contact.type === 'primary');
}

function addressFields(address) {
  // The primary's residential country is fixed at registration and readonly here, so it
  // always comes from Ellis. Only a co-applicant can change theirs.
  const residential = isPrimary(address)
    ? ADDRESS_FIELDS.residential.filter((field) => { return field !== 'country'; })
    : ADDRESS_FIELDS.residential;

  return {
    residential: pickFields(address.residential, residential),
    mailing: pickFields(address.mailing, ADDRESS_FIELDS.mailing),
  };
}

export function autoSave(applicationId, values, sessionStorage = window.sessionStorage) {
  if (!applicationId || !values) return;

  const saved = {
    identities: keyByContact(values.identities, identityFields),
    addresses: keyByContact(values.addresses, addressFields),
  };

  // Nothing left to auto-save (form not initialized yet, or both rows already saved).
  // Deliberately does not clear: only a successful submit clears.
  if (!Object.keys(saved.identities).length && !Object.keys(saved.addresses).length) return;

  try {
    setSessionStorage(storageKey(applicationId), JSON.stringify(saved), sessionStorage);
  } catch (e) {
    // Storage unavailable or over quota - auto-save is best-effort.
  }
}

export function loadAutoSave(applicationId, sessionStorage = window.sessionStorage) {
  if (!applicationId) return null;
  if (snapshotApplicationId === applicationId) return snapshot;

  let stored = null;
  try {
    stored = JSON.parse(getSessionStorage(storageKey(applicationId), sessionStorage));
  } catch (e) {
    stored = null;
  }

  snapshotApplicationId = applicationId;
  snapshot = stored && stored.identities && stored.addresses ? stored : null;
  return snapshot;
}

// Called when the form unmounts, so coming back to the page - from Back, or from Save
// for later - re-reads what was auto-saved while the user was away. Within a single
// mount the snapshot stays frozen so auto-saving cannot feed back into initialValues
// and retrigger enableReinitialize mid-typing.
export function resetAutoSaveSnapshot() {
  snapshotApplicationId = null;
  snapshot = null;
}

export function clearAutoSave(applicationId, sessionStorage = window.sessionStorage) {
  if (!applicationId) return;
  try {
    clearSessionStorage(storageKey(applicationId), sessionStorage);
  } catch (e) {
    // Storage unavailable - clearing runs inside the submit .then(), so a failed removal
    // must not turn an application that saved into a submission error.
  }
  snapshotApplicationId = applicationId;
  snapshot = null;
}

function restoreIdentity(identity, fields) {
  return Object.assign({}, identity, fields);
}

function restoreAddress(address, fields) {
  return Object.assign({}, address, {
    residential: Object.assign({}, address.residential, fields.residential),
    mailing: Object.assign({}, address.mailing, fields.mailing),
  });
}

function restoreEntries(entries, saved, restore) {
  return (entries || []).map((entry) => {
    const key = contactId(entry);
    const fields = key && !entry.id ? saved[key] : null;
    return fields ? restore(entry, fields) : entry;
  });
}

// Merges auto-saved values into server-derived initialValues. Server values win
// wherever a row exists; auto-saved values only fill entries never saved.
export function applyAutoSave(applicationId, initialValues, sessionStorage = window.sessionStorage) {
  const saved = loadAutoSave(applicationId, sessionStorage);
  if (!saved) return initialValues;

  return Object.assign({}, initialValues, {
    identities: restoreEntries(initialValues.identities, saved.identities, restoreIdentity),
    addresses: restoreEntries(initialValues.addresses, saved.addresses, restoreAddress),
  });
}
